import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search, Filter } from 'lucide-react';

const DataTable = ({
  data = [],
  columns = [],
  onRowClick,
  onSort,
  onFilter,
  onSearch,
  onPageChange,
  onPageSizeChange,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 10,
  loading = false,
  searchable = true,
  filterable = true,
  sortable = true,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  className = '',
  emptyMessage = 'No data found',
  showPagination = true,
  showSearch = true,
  showFilters = true,
  customActions = null,
  serverPagination = false,
  serverFiltering = false,
  serverSorting = false
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Client-side filtering, sorting, and pagination
  const processedData = useMemo(() => {
    let filteredData = [...data];

    if (!serverFiltering) {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredData = filteredData.filter(row => {
          return columns.some(column => {
            if (column.filterable === false) return false;
            const value = column.accessor ? row[column.accessor] : row[column.key];
            return value && String(value).toLowerCase().includes(searchLower);
          });
        });
      }

      // Apply column filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          const column = columns.find(col => col.key === key);
          if (column && column.filterable !== false) {
            filteredData = filteredData.filter(row => {
              const cellValue = column.accessor ? row[column.accessor] : row[column.key];

              if (column.filterType === 'select') {
                return String(cellValue) === value;
              } else if (column.filterType === 'date') {
                // Handle date range filtering
                if (value.start || value.end) {
                  const cellDate = new Date(cellValue);
                  if (isNaN(cellDate.getTime())) return false;

                  if (value.start && value.end) {
                    const startDate = new Date(value.start);
                    const endDate = new Date(value.end);
                    endDate.setHours(23, 59, 59, 999); // Include the entire end date
                    return cellDate >= startDate && cellDate <= endDate;
                  } else if (value.start) {
                    const startDate = new Date(value.start);
                    return cellDate >= startDate;
                  } else if (value.end) {
                    const endDate = new Date(value.end);
                    endDate.setHours(23, 59, 59, 999); // Include the entire end date
                    return cellDate <= endDate;
                  }
                }
                return false;
              } else {
                return String(cellValue).toLowerCase().includes(value.toLowerCase());
              }
            });
          }
        }
      });
    }

    // Apply sorting
    if (!serverSorting && sortConfig.key) {
      filteredData.sort((a, b) => {
        const column = columns.find(col => col.key === sortConfig.key);
        if (!column) return 0;

        const aValue = column.accessor ? a[column.accessor] : a[column.key];
        const bValue = column.accessor ? b[column.accessor] : b[column.key];

        if (column.type === 'date') {
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredData;
  }, [data, searchTerm, filters, sortConfig, columns, serverFiltering, serverSorting]);

  // Client-side pagination
  const paginatedData = useMemo(() => {
    if (serverPagination) return processedData;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return processedData.slice(startIndex, endIndex);
  }, [processedData, currentPage, pageSize, serverPagination]);

  // Update total count and pages based on filtered data
  const totalFilteredCount = serverPagination ? (totalCount || 0) : processedData.length;
  const totalFilteredPages = serverPagination
    ? (totalPages || Math.ceil(Math.max(totalFilteredCount, 1) / pageSize))
    : Math.ceil(totalFilteredCount / pageSize);

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
    if (serverSorting) {
      onSort?.(key, direction);
    }
    // Reset to first page when sorting changes
    onPageChange?.(1);
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    onSearch?.(term);
    onPageChange?.(1); // Reset to first page when search changes
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilter?.(newFilters);
    onPageChange?.(1); // Reset to first page when filters change
  };

  // Clear individual filter
  const clearFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    onFilter?.(newFilters);
    onPageChange?.(1); // Reset to first page when filter is cleared
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({});
    setSearchTerm('');
    onFilter?.({});
    onSearch?.('');
    onPageChange?.(1); // Reset to first page when all filters are cleared
  };

  // Handle row selection
  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(paginatedData.map(row => row.id || row._id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (rowId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedRows, rowId]);
    } else {
      onSelectionChange?.(selectedRows.filter(id => id !== rowId));
    }
  };

  // Render cell content
  const renderCell = (row, column) => {
    const value = column.accessor ? row[column.accessor] : row[column.key];

    if (column.render) {
      return column.render(value, row);
    }

    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }

    if (column.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return value != null ? String(value) : '';
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className={`admin-card overflow-hidden ${className}`}>
      {/* Header with search and filters */}
      {(showSearch || showFilters || customActions) && (
        <div className="admin-card-header flex-col items-stretch gap-3">
          <div className="w-full flex flex-wrap items-center justify-between gap-3">
            {/* Search */}
            {showSearch && (
              <div className="flex-1 min-w-[220px] max-w-md">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white transition-all placeholder:text-gray-400"
                  />
                </div>
              </div>
            )}

            {/* Filters and Custom actions grouped together on the right */}
            {(showFilters || customActions) && (
              <div className="ml-auto flex items-center gap-3">
                {/* Filter toggle */}
                {showFilters && (
                  <button
                    onClick={() => setShowFilterPanel(!showFilterPanel)}
                    className={`flex items-center px-2.5 py-1 text-sm font-medium rounded-md transition-all ${
                      showFilterPanel
                        ? 'bg-primary-50 text-primary-700 border border-primary-200'
                        : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <Filter className="w-3.5 h-3.5 mr-1.5" />
                    Filters
                    {Object.keys(filters).length > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-primary-600 text-white rounded-full">
                        {Object.keys(filters).length}
                      </span>
                    )}
                  </button>
                )}

                {/* Custom actions */}
                {customActions}
              </div>
            )}
          </div>

          {/* Filter panel */}
          {showFilterPanel && showFilters && (
            <div className="w-full admin-filter-panel">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filter Options</h4>
                {Object.keys(filters).length > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
              
              {/* Separate text filters and date filters */}
              {(() => {
                const textColumns = columns.filter(col => col.filterable !== false && col.filterType !== 'date' && col.filterType !== 'select');
                const dateColumns = columns.filter(col => col.filterable !== false && col.filterType === 'date');
                const selectColumns = columns.filter(col => col.filterable !== false && col.filterType === 'select');
                
                return (
                  <div className="space-y-3">
                    {/* Text and Select Filters - First Row */}
                    {(textColumns.length > 0 || selectColumns.length > 0) && (
                      <div className="admin-filter-grid">
                        {[...textColumns, ...selectColumns].map(column => (
                          <div key={column.key}>
                            <label className="admin-filter-label">{column.label}</label>
                            <div className="relative">
                              {column.filterType === 'select' ? (
                                <select
                                  value={filters[column.key] || ''}
                                  onChange={(e) => handleFilterChange(column.key, e.target.value)}
                                  className="admin-filter-control"
                                >
                                  <option value="">All</option>
                                  {column.filterOptions?.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  placeholder={`Filter by ${column.label}`}
                                  value={filters[column.key] || ''}
                                  onChange={(e) => handleFilterChange(column.key, e.target.value)}
                                  className="admin-filter-control"
                                />
                              )}
                              {filters[column.key] && (
                                <button
                                  onClick={() => clearFilter(column.key)}
                                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                  type="button"
                                  title="Clear filter"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Date Filters - Second Row */}
                    {dateColumns.map(column => (
                      <div key={column.key}>
                        <label className="admin-filter-label">{column.label}</label>
                        <div className="space-y-2.5">
                          {/* Date range presets */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
                                handleFilterChange(column.key, {
                                  start: startOfDay.toISOString().split('T')[0],
                                  end: endOfDay.toISOString().split('T')[0]
                                });
                              }}
                              className="admin-filter-pill"
                            >
                              Today
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                const yesterday = new Date(today);
                                yesterday.setDate(yesterday.getDate() - 1);
                                const yesterdayStr = yesterday.toISOString().split('T')[0];
                                handleFilterChange(column.key, {
                                  start: yesterdayStr,
                                  end: yesterdayStr
                                });
                              }}
                              className="admin-filter-pill"
                            >
                              Yesterday
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                const weekAgo = new Date(today);
                                weekAgo.setDate(weekAgo.getDate() - 7);
                                handleFilterChange(column.key, {
                                  start: weekAgo.toISOString().split('T')[0],
                                  end: today.toISOString().split('T')[0]
                                });
                              }}
                              className="admin-filter-pill"
                            >
                              Last 7 days
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                const monthAgo = new Date(today);
                                monthAgo.setMonth(monthAgo.getMonth() - 1);
                                handleFilterChange(column.key, {
                                  start: monthAgo.toISOString().split('T')[0],
                                  end: today.toISOString().split('T')[0]
                                });
                              }}
                              className="admin-filter-pill"
                            >
                              Last 30 days
                            </button>
                          </div>
                          {/* Custom date range inputs */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="admin-filter-label">From</label>
                              <div className="relative">
                                <input
                                  type="date"
                                  value={filters[column.key]?.start || ''}
                                  onChange={(e) => {
                                    const currentFilter = filters[column.key] || {};
                                    handleFilterChange(column.key, {
                                      ...currentFilter,
                                      start: e.target.value
                                    });
                                  }}
                                  className="admin-filter-control"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="admin-filter-label">To</label>
                              <div className="relative">
                                <input
                                  type="date"
                                  value={filters[column.key]?.end || ''}
                                  onChange={(e) => {
                                    const currentFilter = filters[column.key] || {};
                                    handleFilterChange(column.key, {
                                      ...currentFilter,
                                      end: e.target.value
                                    });
                                  }}
                                  className="admin-filter-control"
                                />
                              </div>
                            </div>
                          </div>
                          {(filters[column.key]?.start || filters[column.key]?.end) && (
                            <button
                              onClick={() => clearFilter(column.key)}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                              type="button"
                            >
                              Clear date filter
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/50">
              {selectable && (
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === processedData.length && processedData.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                    sortable && column.sortable !== false
                      ? 'cursor-pointer hover:text-gray-900 transition-colors select-none'
                      : ''
                  } ${column.key === 'permissions' ? 'min-w-[200px] max-w-[300px]' : ''}`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="flex items-center gap-1.5">
                    <span>{column.label}</span>
                    {sortable && column.sortable !== false && (
                      <span className={sortConfig.key === column.key ? 'text-primary-600' : 'text-gray-300'}>
                        {getSortIcon(column.key)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-6 py-16 text-center"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                    <span className="text-sm text-gray-500">Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-6 py-16 text-center"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Search className="w-5 h-5 text-gray-400" />
                    </div>
                    <span className="text-sm text-gray-500">{emptyMessage}</span>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => (
                <tr
                  key={row.id || row._id || index}
                  className={`group transition-colors ${
                    onRowClick
                      ? 'cursor-pointer hover:bg-primary-50/50'
                      : 'hover:bg-gray-50/50'
                  }`}
                  onClick={() => onRowClick?.(row)}
                >
                  {selectable && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id || row._id)}
                        onChange={(e) => handleSelectRow(row.id || row._id, e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-4 py-3 text-sm text-gray-700 ${
                        column.key === 'permissions' ? 'min-w-[200px] max-w-[300px]' : ''
                      }`}
                    >
                      {renderCell(row, column)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="admin-card-footer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {(() => {
                const hasItems = totalFilteredCount > 0;
                const start = hasItems ? ((currentPage - 1) * pageSize) + 1 : 0;
                const end = hasItems ? Math.min(currentPage * pageSize, totalFilteredCount) : 0;
                return (
                  <p className="text-xs text-gray-600">
                    Showing <span className="font-medium text-gray-900">{start}</span> to{' '}
                    <span className="font-medium text-gray-900">{end}</span> of{' '}
                    <span className="font-medium text-gray-900">{totalFilteredCount}</span>
                  </p>
                );
              })()}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange?.(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors cursor-pointer"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {totalFilteredPages > 1 && (
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => onPageChange?.(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>

                {Array.from({ length: Math.min(5, totalFilteredPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange?.(pageNum)}
                      className={`min-w-[28px] h-7 text-xs font-medium rounded-md transition-all ${
                        currentPage === pageNum
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => onPageChange?.(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
